import {Box, Button, makeStyles, Tooltip, useTheme} from "@material-ui/core";
import LockIcon from "@material-ui/icons/Lock";
import {grey} from "@material-ui/core/colors";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import CalendarIcon from "@material-ui/icons/EventNote";
import StatusIcon from "@material-ui/icons/RadioButtonUnchecked";
import PeopleIcon from "@material-ui/icons/People";
import ViewQuiltIcon from "@material-ui/icons/ViewQuilt";
import format from "tools/formats/formatDate";
import {Locale} from "date-fns";
import React, {useContext} from "react";
import {apiV2} from "api/firebase";
import {invalidateTaskTimelineQueries} from "api/taskTimelineCache";
import {selectUser} from "store/auth/selectors";
import {useSelector} from "react-redux";
import DataRow from "view/components/DataRow";
import ApprovalIcon from "view/images/TaskIcon";
import BookRoundedIcon from "@material-ui/icons/BookRounded";
import ArrowForward from "@material-ui/icons/ArrowForward";
import AccessTime from "@material-ui/icons/AccessTime";
import {useTranslate} from "@tolgee/react";
import useDateFnsLocale from "hooks/useDateFnsLocale";

import type {ApprovalFormValue} from "view/components/TasksList/types";
import {LocalMapContext} from "view/context/LocalMapContext";
import UnpublishedStatus from "view/apps/Organiser/pages/Show/OnlineManual/components/TaskDetailsDrawer/components/UnpublishedStatus";
import TodayOutlinedIcon from "@material-ui/icons/TodayOutlined";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import {
  useCurrentShow,
  useCurrentShowBuildTypes,
  useCurrentShowPackages,
  useOrganiser,
} from "hooks";
import {getReadableString} from "constraints/general";
import Tags from "view/components/Tags";
import TagIcon from "view/images/TagIcon";
import {
  IOrganiserCompanyTaskTag,
  ITaskTimeline,
  TaskTimeLineType,
  Roles,
  TaskApprovalType,
  TaskPackageMode,
  TaskStatus,
} from "@evennt-tech/api-v2-core";
import {TASK_STATUS_ENUM} from "constraints/taskd";
import useStandsByIds from "hooks/stand/useStandsByIds";
import useStandsCount from "hooks/stand/useStandsCount";
import {PackageMode} from "view/components/TaskDrawer/AssignToRow/shared";
import AssignToRow from "./AssignToRow";
import ModalSectionHeader from "../Drawer/ModalSectionHeader";
import ClipboardButton from "../TasksList/components/ClipboardButton";
import CopyReviewLinkButton from "./CopyReviewLinkButton";
import TaskMultipleReviewersApprovalDropdown from "./TaskMultipleReviewersApprovalDropdown";
import {formatDuration, splitEmailReviewers} from "./helpers";
import ActivityHistory from "./SettingsRows/HistoryActivity";

type EventFunctionType = (
  e: React.MouseEvent<HTMLButtonElement, MouseEvent>
) => void;

type ActionPanelProps = {
  onClick?: EventFunctionType;
  id?: string;
  isApprovalLink?: boolean;
};

type TextActionPanelProps = {
  onClick: EventFunctionType;
  title: string;
};

export type EditTagArgs = {
  id: string;
  text: string;
  type: "edit";
};

type RemoveTagArgs = {
  id: string;
  type: "remove";
  isDBRemoval: boolean;
};

export type AddTagByTextArgs = {
  isNew: true;
  shouldAddToEntity?: boolean;
  text: string;
  /**
   * Client-side handle of the row the tag came from, so the caller can map the
   * id the server mints back onto whatever was staged against it (translations).
   */
  localKey?: string;
};

export type AddTagByIdArgs = {
  isNew: false;
  id: string;
};

export type AddTagArgs = (AddTagByTextArgs | AddTagByIdArgs) & {
  type: "add";
};

export type ModifyTagArgs = EditTagArgs | RemoveTagArgs | AddTagArgs;

/**
 * Creating a tag resolves to the row the server minted — every other branch
 * resolves to nothing.
 */
export type ModifyTagResult = {id: string} | void;

const useCustomActionPanelStyle = makeStyles(() => ({
  label: {
    flex: "0 0 180px",
    color: grey[600],
    textTransform: "uppercase",
  },
  value: {
    flex: 3,
    fontWeight: 400,
  },
  actionPanel: {
    width: 220,
    display: "flex",
    justifyContent: "flex-end",
  },
}));

const TextActionPanel = ({onClick, title}: TextActionPanelProps) => {
  const theme = useTheme();

  return (
    <Button
      onClick={onClick}
      style={{
        padding: 8,
        color: theme.palette.primary.main,
        display: "flex",
        alignItems: "center",
      }}
    >
      {title} <ArrowForward />
    </Button>
  );
};

export type TaskSettingsProps = {
  isUnpublished?: boolean;
  onChangeStatus?: () => Promise<any>;
  packageMode?: PackageMode | TaskPackageMode | null;
  approvalText?: string;
  isDraft?: boolean;
  className?: string;
  status?: string;
  statusColor?: string;
  buildTypes?: string[];
  packageIds?: string[];
  stands?: string[];
  dueDateComponent?: React.ReactNode;
  availableDate?: Date;
  headless?: boolean;
  onEditApproval?: EventFunctionType;
  onEditTaskStatus?: () => void;
  onEditAssignTo?: () => void;
  onEditDueDate?: EventFunctionType;
  onEditAvailableDate?: EventFunctionType;
  onEditTaskTags?: EventFunctionType;
  isApprovalByEmail?: boolean;
  showId?: string;
  taskId?: string;
  companyId?: string;
  standId?: string;
  exhibitorCompanyName?: string;
  exhibitorCompanyNameSuffix?: string;
  standName?: string;
  standSuffix?: string;
  approval?: ApprovalFormValue;
  // Email reviewers (single EMAIL approval or email-typed MULTIPLE_REVIEWERS
  // slots). Each renders a "copy review link" row to a reviewer's magic-link.
  emailReviewers?: {email: string}[];
  taskTags?: IOrganiserCompanyTaskTag<string>[];
  tags?: string[];
  multipleReviewers?: any;
  showOrganiserList?: any;
  submittedDateComponent?: React.ReactNode;
  submittedByComponent?: React.ReactNode;
  certificateName?: string;
  // Latest timeline action, forwarded to ActivityHistory's inline row.
  // `null` = no activity yet; `undefined` = ActivityHistory fetches its own.
  lastHistoryAction?: ITaskTimeline | null;
  isConditional?: boolean;
  inStatusTime?: Date | string | null;
  modifyTag?: (args: ModifyTagArgs) => Promise<ModifyTagResult>;
  /** FS-2877 S4.3: inherited show task — tags are master-owned, so the row is
   * shown READ-ONLY (chips only, padlock, no add/remove/edit). */
  isInherited?: boolean;
  readonly?: boolean;
};

export const getDateText = (
  date?: Date | null,
  dateFormat?: string,
  locale?: Locale
) => {
  return date ? format(date, dateFormat || "d LLL, yyyy", locale) : "-";
};

const TaskSettings: React.FC<TaskSettingsProps> = ({
  className,
  headless,
  approvalText,
  isUnpublished,
  status,
  statusColor,
  buildTypes,
  packageIds,
  stands,
  dueDateComponent,
  availableDate,
  onEditApproval,
  packageMode,
  onEditAssignTo,
  onEditDueDate,
  onEditAvailableDate,
  onEditTaskStatus,
  isApprovalByEmail,
  showId,
  isConditional,
  taskId,
  companyId,
  standId,
  exhibitorCompanyName,
  exhibitorCompanyNameSuffix,
  standName,
  standSuffix,
  approval,
  emailReviewers,
  onChangeStatus = async () => ({}),
  taskTags,
  tags,
  modifyTag,
  isInherited,
  onEditTaskTags,
  multipleReviewers,
  showOrganiserList,
  submittedByComponent,
  submittedDateComponent,
  certificateName,
  lastHistoryAction,
  inStatusTime,
  isDraft,
}) => {
  const localMap = useContext(LocalMapContext);
  const {t} = useTranslate();
  const locale = useDateFnsLocale();
  const [show] = useCurrentShow({showId});
  const [buildTypesInShow] = useCurrentShowBuildTypes();
  const [packagesInShow] = useCurrentShowPackages();

  const assignRowShowId = onEditAssignTo ? showId : undefined;
  const [assignedStands] = useStandsByIds({
    showId: assignRowShowId,
    ids: stands,
  });
  const [standsInShowCount] = useStandsCount(assignRowShowId);

  const ActionPanel: React.FC<ActionPanelProps> = ({
    onClick,
    id,
    isApprovalLink = false,
  }) => {
    const isNotEditable = !onClick && !isApprovalLink;
    if (isNotEditable) {
      return null;
    }

    const renderIcon = () => {
      if (isApprovalLink) {
        return (
          <ClipboardButton
            showId={showId}
            taskId={taskId}
            companyId={companyId}
            standId={standId}
            email={
              approval?.type === TaskApprovalType.EMAIL
                ? approval?.value
                : undefined
            }
          />
        );
      }
      return (
        <IconButton id={id} onClick={onClick} style={{padding: 8}}>
          <EditIcon htmlColor={grey[500]} />
        </IconButton>
      );
    };

    return renderIcon();
  };

  const customActionPanelStyle = useCustomActionPanelStyle();
  const {role} = useSelector(selectUser);

  const changeStatusInReview = async () => {
    if (showId && taskId && standId && status) {
      await apiV2.task.changeTaskInstanceStatus(
        {taskId},
        {
          showId,
          standId,
          status:
            status === TASK_STATUS_ENUM.inReview
              ? TaskStatus.WAITING_FOR_APPROVAL
              : TaskStatus.IN_REVIEW,
        }
      );
      await onChangeStatus();
      invalidateTaskTimelineQueries({showId, standId, taskId});
    }
  };

  const isOrganiser = role === "organiser";
  const isReviewStatus =
    (status === "In Review" || status === "Waiting for Approval") &&
    isOrganiser;
  const oppositeStatusText =
    status === "In Review" ? "Waiting for Approval" : "In Review";
  const showApprovalActionButton =
    !isApprovalByEmail && isOrganiser ? onEditApproval : undefined;
  // Rejected tasks: hide the edit-status pencil entirely — the organiser must
  // not change status out of Rejected (the exhibitor resubmits instead). The
  // change-status endpoint is guarded server-side too. Done keeps the pencil.
  const showChangeStatusButton =
    status === "Done" && isOrganiser ? onEditTaskStatus : undefined;
  const showChangeReviewStatusButton =
    isReviewStatus && isOrganiser ? changeStatusInReview : undefined;
  // A single-reviewer EMAIL approval carries its "copy review link" button on
  // the "Approval" row itself; only MULTIPLE_REVIEWERS (which replaces that
  // row with the reviewers dropdown) needs standalone "Review link" rows.
  const {approvalRowReviewer, standaloneReviewers} = splitEmailReviewers(
    emailReviewers,
    approval?.type === TaskApprovalType.MULTIPLE_REVIEWERS
  );

  const renderStatus = () => {
    if (!status) {
      return null;
    }
    if (isUnpublished) {
      return <UnpublishedStatus status={status} />;
    }
    return status;
  };
  const statusPanel = isReviewStatus ? (
    <TextActionPanel
      title={oppositeStatusText?.toUpperCase()}
      onClick={showChangeReviewStatusButton as EventFunctionType}
    />
  ) : (
    <ActionPanel
      id="cy-approval-pencil-button"
      onClick={showChangeStatusButton}
    />
  );

  const addTag =
    !isInherited && modifyTag
      ? async (text: string) => {
          const taskTag = taskTags?.find((taskTag) => taskTag.name === text);
          if (taskTag) {
            await modifyTag({type: "add", isNew: false, id: taskTag.id});
            return;
          }
          await modifyTag({
            type: "add",
            isNew: true,
            text,
            shouldAddToEntity: true,
          });
        }
      : undefined;
  const removeTag =
    !isInherited && modifyTag
      ? async (id: string) => {
          await modifyTag({type: "remove", id, isDBRemoval: false});
        }
      : undefined;

  return (
    <Box className={className} marginBottom={3}>
      {!headless && <ModalSectionHeader title="Settings" />}
      <Box>
        {status && (
          <DataRow
            label={t("common.tasks.settings.status")}
            value={renderStatus()}
            textColor={statusColor}
            icon={<StatusIcon htmlColor={grey[500]} />}
            classes={isReviewStatus ? customActionPanelStyle : undefined}
            actionPanel={statusPanel}
          />
        )}
        {approvalText &&
          approval?.type === TaskApprovalType.MULTIPLE_REVIEWERS && (
            <TaskMultipleReviewersApprovalDropdown
              multipleReviewers={multipleReviewers}
              showOrganiserList={showOrganiserList}
              taskStatus={status}
              propsClasses={customActionPanelStyle}
              isReviewStatus={isReviewStatus}
            />
          )}
        {approvalText && approval?.type !== "multipleReviewers" && (
          <DataRow
            label={t("common.tasks.approval")}
            value={approvalText}
            icon={<ApprovalIcon />}
            classes={isReviewStatus ? customActionPanelStyle : undefined}
            actionPanel={
              isReviewStatus && approvalRowReviewer ? (
                <CopyReviewLinkButton
                  showId={showId}
                  taskId={taskId}
                  standId={standId}
                  reviewerEmail={approvalRowReviewer.email}
                />
              ) : (
                <ActionPanel
                  id="cy-approval-pencil-button"
                  onClick={showApprovalActionButton}
                  isApprovalLink={isApprovalByEmail}
                />
              )
            }
          />
        )}
        {/* "Copy review link" per email reviewer, shown only while the task is
            awaiting review (In Review / Waiting for Approval + organiser) —
            before the exhibitor submits there's nothing to review. */}
        {isReviewStatus &&
          standaloneReviewers.map((reviewer) => (
            <DataRow
              key={reviewer.email}
              label="Review link"
              value={reviewer.email}
              icon={<ApprovalIcon />}
              classes={isReviewStatus ? customActionPanelStyle : undefined}
              actionPanel={
                <CopyReviewLinkButton
                  showId={showId}
                  taskId={taskId}
                  standId={standId}
                  reviewerEmail={reviewer.email}
                />
              }
            />
          ))}
        {standName && (
          <DataRow
            label={t("common.tasks.settings.stand_number", {
              stand: localMap.stand,
            })}
            value={standName}
            icon={<ViewQuiltIcon htmlColor={grey[500]} />}
            actionPanel={<div />}
            classes={isReviewStatus ? customActionPanelStyle : undefined}
          />
        )}
        {standSuffix && role !== "exhibitor" && (
          <DataRow
            label={`${localMap.stand} Number Suffix`}
            value={standSuffix}
            icon={<ViewQuiltIcon htmlColor={grey[500]} />}
            actionPanel={<div />}
            classes={isReviewStatus ? customActionPanelStyle : undefined}
          />
        )}
        {exhibitorCompanyName && (
          <DataRow
            label={localMap.exhibitor}
            value={exhibitorCompanyName}
            icon={<PeopleIcon htmlColor={grey[500]} />}
            actionPanel={<div />}
            classes={isReviewStatus ? customActionPanelStyle : undefined}
          />
        )}
        {exhibitorCompanyNameSuffix && role !== "exhibitor" && (
          <DataRow
            label="Company Name Suffix"
            value={exhibitorCompanyNameSuffix}
            icon={<PeopleIcon htmlColor={grey[500]} />}
            actionPanel={<div />}
            classes={isReviewStatus ? customActionPanelStyle : undefined}
          />
        )}
        {onEditAssignTo && (
          <AssignToRow
            isConditional={isConditional}
            newStands={assignedStands}
            standsInShowCount={standsInShowCount}
            buildTypes={buildTypes}
            packageIds={packageIds}
            stands={stands}
            buildTypesInShow={buildTypesInShow}
            packagesInShow={packagesInShow}
            packageMode={packageMode}
            onEditAssignTo={onEditAssignTo}
          />
        )}
        {role === "organiser" && certificateName && (
          <DataRow
            label="Certificate"
            value={certificateName}
            icon={<BookRoundedIcon htmlColor={grey[500]} />}
            classes={isReviewStatus ? customActionPanelStyle : undefined}
          />
        )}
        {availableDate && (
          <DataRow
            label={t("common.tasks.settings.available_date")}
            value={getDateText(availableDate, getReadableString(show), locale)}
            icon={<CalendarIcon htmlColor={grey[500]} />}
            actionPanel={
              <ActionPanel
                id="cy-available-date-pencil-button"
                onClick={onEditAvailableDate}
              />
            }
            classes={isReviewStatus ? customActionPanelStyle : undefined}
          />
        )}
        {inStatusTime && !isDraft && (
          <DataRow
            label={t("common.tasks.settings.time_in_status")}
            value={formatDuration(inStatusTime, locale)}
            classes={isReviewStatus ? customActionPanelStyle : undefined}
            icon={<AccessTime htmlColor={grey[500]} />}
          />
        )}
        {taskId && !!standId && show && (
          <ActivityHistory
            taskId={taskId}
            show={show}
            isInReview={isReviewStatus}
            standId={standId}
            dateFormat={getReadableString(show)}
            lastAction={lastHistoryAction}
          />
        )}
        <DataRow
          label={t("common.tasks.settings.due_date")}
          value={dueDateComponent}
          icon={<CalendarIcon htmlColor={grey[500]} />}
          actionPanel={
            <ActionPanel
              id="cy-due-date-pencil-button"
              onClick={onEditDueDate}
            />
          }
          classes={isReviewStatus ? customActionPanelStyle : undefined}
        />
        {(onEditTaskTags || isInherited) && (
          <DataRow
            label="Tags"
            value={
              <Tags
                addTag={addTag}
                removeTag={removeTag}
                tags={tags}
                tagDefinitions={taskTags}
              />
            }
            icon={<TagIcon />}
            actionPanel={
              isInherited ? (
                <Tooltip title="Managed in the Library — edit it from the master task.">
                  <LockIcon fontSize="small" color="disabled" />
                </Tooltip>
              ) : (
                <ActionPanel
                  id="cy-due-date-pencil-button"
                  onClick={onEditTaskTags}
                />
              )
            }
            classes={isReviewStatus ? customActionPanelStyle : undefined}
          />
        )}
        {submittedDateComponent && (
          <DataRow
            label={t("common.tasks.settings.submitted_date")}
            value={submittedDateComponent}
            icon={<TodayOutlinedIcon htmlColor={grey[500]} />}
            classes={isReviewStatus ? customActionPanelStyle : undefined}
          />
        )}
        {submittedByComponent && (
          <DataRow
            label={t("common.tasks.settings.submitted_by")}
            value={submittedByComponent}
            icon={<AccountCircleIcon htmlColor={grey[500]} />}
            classes={isReviewStatus ? customActionPanelStyle : undefined}
            last
          />
        )}
      </Box>
    </Box>
  );
};

export default TaskSettings;
